
import React, { useState } from 'react';
import { Button, TextField, Box } from '@mui/material';
import Modal from 'react-bootstrap/Modal';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error'; 
import config from '../config';
import { DAPP_NAME, TransactionName } from '../constants';
import { ExecuteSubmitTransaction } from '../utils';
import MyVerticallyCenteredModal from './MyVerticallyCenteredModal';

const getErrorMessage = (message) => {
  try {
    if (message.startsWith('Error:')) {
      message = message.replace('Error: ', '');
    }

    if (message.startsWith('{')) {
      const parsedError = JSON.parse(message);

      if (parsedError?.message?.error === 'insufficient balance') {
        return 'Not Authorized!';
      }

      return parsedError.message?.error || 'Unknown Error';
    }

    if (message === 'insufficient balance') {
      return 'Not Authorized!';
    }

    return message;

  } catch (err) {
    return message || 'An unexpected error occurred.';
  }
};

const TemporaryWhitelist = ({ goBack }) => {
  const [merchantId, setMerchantId] = useState('');
  const [allowedUntil, setAllowedUntil] = useState('');
  const [allowedUntilError, setAllowedUntilError] = useState(false); // Track input error
  const [responseMessage, setResponseMessage] = useState("");
  const [modalShow, setModalShow] = useState(false);
  const [inputError, setInputError] = useState(false); // Track input error state

  const validateInput = () => {
    if (!merchantId) {
      setInputError(true);
      return false;
    }
    setInputError(false);
    return true;
  };

  const handleTemporaryWhitelist = async () => {
    if (!allowedUntil) {
      setAllowedUntilError(true); // Highlight field if not filled
      return;
    }

    if (!validateInput()) return; // Prevent execution if input is invalid

    try {
    const utcTime = convertToUTCTimestamp(allowedUntil);

    const transactionParams = [
      merchantId
    ];

    const [res, errMsg] = await ExecuteSubmitTransaction(config.CHANNEL_NAME, config.CHAINCODE_NAME, TransactionName.WHITELIST, transactionParams);
    if (res) {
      setResponseMessage(res.transactionId ? `Transaction successful: ${res.transactionId}` : `Error: ${res.error}`);
    } else {
      throw new Error(errMsg);
    }
  }catch(error) {
    setResponseMessage('An error occurred during the transaction.');
  } finally {
    setModalShow(true);
  }
  
  };

  const convertToUTCTimestamp = (inputTime) => {
    const date = new Date(inputTime);
    if (isNaN(date.getTime())) throw new Error('Invalid date format');
    return date.toISOString();
  };

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      width: '100%',
      padding: '20px',
    }}>
      <Button
        onClick={goBack}
        startIcon={<ArrowBackIcon />}
        sx={{
          alignSelf: 'flex-start',
          marginBottom: '20px',
          textTransform: 'none',
        }}
      >
        Back
      </Button>

      <TextField
        placeholder="Enter User's Address"
        variant="outlined"
        value={merchantId}
        onChange={(e) => setMerchantId(e.target.value)}
        fullWidth
        error={inputError} // Apply error state
        helperText={inputError ? "Required" : ""}
        FormHelperTextProps={{
          sx: {
            backgroundColor: 'transparent',  // Remove any background color
            marginLeft: 0,  // Align the helper text with the input field
            color: 'red',  // Set text color to red for visibility
          },
        }}
        sx={{
          backgroundColor: 'transaparent',
          borderRadius: '8px',
          marginBottom: '20px',
          maxWidth: '400px',
          '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
            '& fieldset': {
              borderColor: '#E0E0E0',
            },
            '&:hover fieldset': {
              borderColor: '#E0E0E0',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#1976D2',
            },
          },
        }}
      />
      <TextField
        label="Allowed Until"
        type="datetime-local"
        value={allowedUntil}
        onChange={(e) => {
          setAllowedUntil(e.target.value);
          setAllowedUntilError(false); // Reset error when input changes
        }}
        error={allowedUntilError}  // Highlight with red if error
        helperText={allowedUntilError ? "This field is required" : ""}
        fullWidth
        InputLabelProps={{ shrink: true }}
        sx={{
          backgroundColor: 'white',
          borderRadius: '8px',
          marginBottom: '20px',
          maxWidth: '400px',
          '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
            '& fieldset': {
              borderColor: allowedUntilError ? 'red' : '#E0E0E0',  // Red border on error
            },
            '&:hover fieldset': {
              borderColor: allowedUntilError ? 'red' : '#E0E0E0',
            },
            '&.Mui-focused fieldset': {
              borderColor: allowedUntilError ? 'red' : '#1976D2',
            },
          },
        }}
      />

      <Button
        variant="contained"
        onClick={handleTemporaryWhitelist}
        sx={{
          marginBottom: '10px',
          backgroundColor: '#0055FF',
          borderRadius: '8px',
          height: '45px',
          width: '100%',
          maxWidth: '400px',
          textTransform: 'none',
          fontSize: '16px',
          fontWeight: 'bold',
          '&:hover': {
            backgroundColor: '#0044CC',
          },
        }}
      >
        Whitelist User
      </Button>


      <MyVerticallyCenteredModal
        show={modalShow}
        onHide={() => setModalShow(false)}
        message={responseMessage}
        getErrorMessage={getErrorMessage}
      />

    </Box>
  );
};

export default TemporaryWhitelist;
